import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ModalPredefinedLoading from '../private/modal-sheet-utils/ModalPredefinedLoading.js';
import ModalPredefinedGeneric from '../private/modal-sheet-utils/ModalPredefinedGeneric.js';
import ModalPredefinedInfo from '../private/modal-sheet-utils/ModalPredefinedInfo.js';
const matchMedia = globalThis.window?.matchMedia?.('(prefers-reduced-motion: reduce)');
export const prefersReduceMotion = matchMedia === true || matchMedia?.matches === true;
export const MODAL_DESTROY_AFTER = prefersReduceMotion ? 0 : 400;
export const PREDEFINED_MODALS = {
  loading: {
    Component: ModalPredefinedLoading,
    minimumVisibility: 2000,
    blockingUI: true
  },
  generic: {
    Component: ModalPredefinedGeneric
  },
  info: {
    Component: ModalPredefinedInfo
  }
};
class MinimumVisibility {
  constructor(minimum) {
    this.startedInMs = Date.now();
    this.minimumInMs = minimum;
  }
  getTimeSpentSoFar() {
    return Date.now() - this.startedInMs;
  }
  getLoadingMimimumTimeLeft() {
    return this.minimumInMs - this.getTimeSpentSoFar();
  }
  resolveWhenReachedMinimumOf() {
    const timeLeft = this.getLoadingMimimumTimeLeft();

    // eslint-disable-next-line no-promise-executor-return
    return new Promise(resolve => setTimeout(resolve, timeLeft));
  }
}

// TODO: make ways to declare images to preload!
/**
 * @typedef {Object} ModalSheetProps
 * @property {function(): JSX} Content
 * @property {Object} contentProps
 * @property {boolean} isBlockingUI
 * @property {function(): void} dispatchHide
 * @property {boolean} willHide
 */

/**
 * @typedef UseModalState
 * @property {ModalSheetProps} modalSheetProps
 * @property {string|null} visibleType
 * @property {boolean} isHidden
 * @property {boolean} willHide
 * @property {function(modalConfig: ModalConfig): void} dispatchModal
 * @property {function(): void} dispatchHide
 */
/**
 * @typedef {Object} ModalConfig
 * @property {string} type
 * @property {Object} props
 * @property {boolean} blockingUI
 */
/**
 * @typedef {Object} CustomModalConfig
 * @property {function():JSX} Component
 * @property {number[0]} minimumVisibility
 * @property {boolean[false]} blockingUI
 */
/**
 * @typedef {Object<string,CustomModalConfig>} CustomModals
 */
const useModalDefaultProps = {
  customModals: {}
};

/**
 * @param {CustomModals} customModals
 * @returns {UseModalState}
 */
const useModal = ({
  // No hook should ever rely on this property for anything. Otherwise see ReproPortal4089.js
  customModals
} = useModalDefaultProps) => {
  const findModalType = type => {
    if (Object.prototype.hasOwnProperty.call(PREDEFINED_MODALS, type)) {
      return PREDEFINED_MODALS[type];
    }
    if (Object.prototype.hasOwnProperty.call(customModals, type)) {
      return customModals[type];
    }
    return false;
  };
  /**
   *
   * @type {React.MutableRefObject<MinimumVisibility|null>}
   */
  const minimumCounterRef = useRef(null);
  const [currentModal, setCurrentModal] = useState({
    type: null
  });
  const [willHide, setWillHide] = useState(false);
  const [requestedModal, setRequestedModal] = useState({
    type: null
  });
  const doHide = useCallback(() => {
    setRequestedModal({
      type: null
    });
  }, []);
  const dispatchHide = useCallback(() => {
    if (minimumCounterRef.current) {
      minimumCounterRef.current.resolveWhenReachedMinimumOf().then(() => {
        doHide();
      }).catch(error => {
        console.error('Minimum time left errored', error);
        doHide();
      });
      return;
    }
    doHide();
  }, [doHide]);
  const dispatchModal = useCallback(modal => {
    if (!Object.prototype.hasOwnProperty.call(modal, 'type')) {
      console.error('Requested modal does not conform to ModalConfig type', modal);
      return;
    }
    if (modal.type === null) {
      dispatchHide();
      return;
    }
    const requestedModalConfig = findModalType(modal.type);
    if (requestedModalConfig === false) {
      console.error(`Requested modal type is not defined. Skipping. Type: ${modal.type}`, modal);
      return;
    }
    if (requestedModalConfig.minimumVisibility) {
      minimumCounterRef.current = new MinimumVisibility(requestedModalConfig.minimumVisibility);
    }
    setRequestedModal(modal);
  }, [dispatchHide]);
  useEffect(() => {
    if (currentModal.type === requestedModal.type) {
      // same do nothing
      return () => {};
    }
    // was no modal before
    if (!currentModal.type) {
      setCurrentModal(requestedModal);
      return () => {};
    }
    // was modal but different, next one is modal
    if (requestedModal.type !== null) {
      setCurrentModal(requestedModal);
      return () => {};
    }
    // was modal, hiding it
    const timeout = setTimeout(() => {
      setCurrentModal(requestedModal);
      setWillHide(false);
    }, MODAL_DESTROY_AFTER);
    setWillHide(true);
    return () => {
      clearTimeout(timeout);
    };
  }, [requestedModal, currentModal.type]);
  const currentModalConfig = findModalType(currentModal.type);
  // When dispatching modal it's fine to just tell the type. Props are optional.
  const isBlockingUI = currentModal?.blockingUI || currentModalConfig?.blockingUI;
  return {
    modalSheetProps: {
      Content: currentModal.type ? currentModalConfig.Component : null,
      contentProps: {
        dispatchHide,
        ...(currentModal?.props ?? {})
      },
      isBlockingUI,
      dispatchHide,
      willHide
    },
    visibleType: currentModal.type,
    isHidden: currentModal.type === null,
    dispatchModal,
    dispatchHide,
    willHide
  };
};
export default useModal;